import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import useAuthStore from '../store/authStore';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrors';

function TakeQuiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreviewMode = new URLSearchParams(location.search).get('preview') === '1';
  const { isAuthenticated } = useAuthStore();
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const attemptCreated = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchQuizDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId, isAuthenticated, navigate, isPreviewMode]);

  // Timer countdown
  useEffect(() => {
    if (quiz?.time_limit_minutes && startTime && !isSubmitted) {
      const totalSeconds = quiz.time_limit_minutes * 60;
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const remaining = totalSeconds - elapsed;

        if (remaining <= 0) {
          clearInterval(interval);
          handleSubmitQuiz(true); // Auto-submit when time runs out
        } else {
          setTimeRemaining(remaining);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz, startTime, isSubmitted]);

  const fetchQuizDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/quizzes/quizzes/${quizId}/`);

      const quizData = response.data;
      setQuiz(quizData);

      // Randomize questions if enabled
      let quizQuestions = [...quizData.questions];
      if (quizData.randomize_questions) {
        quizQuestions = quizQuestions.sort(() => Math.random() - 0.5);
      }
      setQuestions(quizQuestions);

      // Create quiz attempt only once
      if (isPreviewMode) {
        setStartTime(Date.now());
      } else if (!attemptCreated.current) {
        attemptCreated.current = true;
        await startQuizAttempt();
      }
    } catch (error) {
      addNotification(t('Failed to load quiz'), 'error');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const startQuizAttempt = async () => {
    try {
      const response = await api.post('/quizzes/attempts/', {
        quiz: parseInt(quizId)
      });
      setAttemptId(response.data.id);
      setStartTime(Date.now());
    } catch (error) {
      addNotification(t('Failed to start quiz attempt'), 'error');
    }
  };

  const handleAnswerChange = (questionId, answerId, isMultiple) => {
    setAnswers(prev => {
      if (isMultiple) {
        const currentAnswers = prev[questionId] || [];
        const isSelected = currentAnswers.includes(answerId);

        return {
          ...prev,
          [questionId]: isSelected
            ? currentAnswers.filter(id => id !== answerId)
            : [...currentAnswers, answerId]
        };
      } else {
        return {
          ...prev,
          [questionId]: [answerId]
        };
      }
    });
  };

  const handleSubmitQuiz = async (autoSubmit = false) => {
    if (!autoSubmit) {
      setShowConfirmModal(true);
      return;
    }

    setSubmitting(true);
    try {
      if (isPreviewMode) {
        const response = await api.post(`/quizzes/quizzes/${quizId}/preview-score/`, { answers });
        setResults({
          ...response.data,
          time_spent_seconds: Math.floor((Date.now() - startTime) / 1000),
        });
        setIsSubmitted(true);
        addNotification(
          response.data.passed
            ? t('Congratulations! You passed the quiz!')
            : t('Quiz submitted. Keep practicing!'),
          response.data.passed ? 'success' : 'error'
        );
        return;
      }

      // Persist and grade the complete attempt atomically. This is one network
      // round trip regardless of how many questions the quiz contains.
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);

      const attemptResponse = await api.post(`/quizzes/attempts/${attemptId}/submit/`, {
        time_spent_seconds: timeSpent,
        answers,
      });

      setResults(attemptResponse.data);
      setIsSubmitted(true);
      addNotification(
        attemptResponse.data.passed
          ? t('Congratulations! You passed the quiz!')
          : t('Quiz submitted. Keep practicing!'),
        attemptResponse.data.passed ? 'success' : 'error'
      );
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to submit quiz'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmSubmit = () => {
    setShowConfirmModal(false);
    handleSubmitQuiz(true);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading || !quiz) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopNav title={t('Loading Quiz...')} />
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted && results) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopNav title={t('Quiz Results')} />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-xl shadow-md p-8">
                <div className="text-center mb-8">
                  {results.passed ? (
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}

                  <h2 className="text-3xl font-bold text-gray-800 mb-2">
                    {results.passed ? t('Congratulations!') : t('Keep Practicing!')}
                  </h2>
                  <p className="text-gray-600">
                    {results.passed ? t('You passed the quiz!') : t('You did not pass this time.')}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">{t('Your Score')}</p>
                    <p className="text-3xl font-bold text-primary-600">{results.score}%</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">{t('Passing Score')}</p>
                    <p className="text-3xl font-bold text-gray-800">{quiz.passing_score}%</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">{t('Points Earned')}</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {results.points_earned || 0} / {results.total_points || 0}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">{t('Time Spent')}</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {results.time_spent_seconds ? formatTime(results.time_spent_seconds) : '0:00'}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => navigate(-1)}
                    className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium"
                  >
                    {t('Back to Lesson')}
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
                  >
                    {t('Retake Quiz')}
                  </button>
                </div>
              </div>

              {/* Per-question review — explanations belong here, once the
                  student has actually answered, not while they're still
                  picking an answer (which would just give it away).
                  Correct/incorrect comes from each response's own
                  is_correct (never masked, it's the student's own attempt)
                  — Answer.is_correct itself stays masked to non-owners even
                  here, so a wrong answer is flagged red without revealing
                  which option was actually right. Teachers can turn the
                  whole section off per-quiz (quiz.show_correct_answers);
                  the backend also masks response.is_correct/points_earned
                  in that case, so this is defense in depth, not the only
                  gate. */}
              {quiz.show_correct_answers && (
              <div className="bg-white rounded-xl shadow-md p-8 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('Review')}</h3>
                <div className="space-y-5">
                  {questions.map((question, index) => {
                    const response = (results?.responses || []).find((r) => r.question === question.id);
                    const selectedIds = response?.selected_answers || answers[question.id] || [];
                    return (
                      <div key={question.id} className="border-t pt-4 first:border-t-0 first:pt-0">
                        <div className="flex items-start justify-between gap-3">
                          <p className="font-medium text-gray-800 flex-1">{index + 1}. {question.text}</p>
                          {response && (
                            <span className={`text-xs font-medium px-2 py-1 rounded-full shrink-0 ${response.is_correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {response.is_correct ? t('Correct') : t('Incorrect')} · {response.points_earned || 0}/{question.points} {t('pts')}
                            </span>
                          )}
                        </div>
                        <ul className="mt-3 space-y-1.5">
                          {(question.answers || []).map((answer) => {
                            const wasSelected = selectedIds.includes(answer.id);
                            return (
                              <li
                                key={answer.id}
                                className={`text-sm flex items-center gap-2 px-3 py-2 rounded-lg border ${
                                  wasSelected ? (response?.is_correct ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50') : 'border-gray-100'
                                }`}
                              >
                                {wasSelected ? (
                                  response?.is_correct ? (
                                    <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  ) : (
                                    <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                  )
                                ) : (
                                  <span className="w-4 h-4 shrink-0" />
                                )}
                                <span className={wasSelected ? (response?.is_correct ? 'text-green-800 font-medium' : 'text-red-800') : 'text-gray-600'}>
                                  {answer.text}
                                </span>
                                {wasSelected && <span className="text-xs text-gray-500 ml-auto">{t('Your answer')}</span>}
                              </li>
                            );
                          })}
                        </ul>
                        {!selectedIds.length && (
                          <p className="text-sm text-gray-500 mt-2 italic">{t('No answer')}</p>
                        )}
                        {question.explanation && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                              <span className="font-semibold">{t('Note:')}</span> {question.explanation}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              )}
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={quiz.title} subtitle={quiz.description} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            {isPreviewMode && (
              <div className="mb-5 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                <span className="font-semibold">{t('Preview score check')}:</span>{' '}
                {t('This result is temporary and will not be added to your quiz history or course progress.')}
              </div>
            )}
            {/* Quiz Header */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{quiz.title}</h2>
                  <p className="text-gray-600">{t('{{count}} Questions • Passing Score: {{score}}%', { count: questions.length, score: quiz.passing_score })}</p>
                </div>
                {quiz.time_limit_minutes && timeRemaining !== null && (
                  <div className={`text-center px-6 py-3 rounded-lg ${timeRemaining < 60 ? 'bg-red-100' : 'bg-primary-100'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('Time Remaining')}</p>
                    <p className={`text-2xl font-bold ${timeRemaining < 60 ? 'text-red-600' : 'text-primary-600'}`}>
                      {formatTime(timeRemaining)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-6">
              {questions.map((question, index) => (
                <div key={question.id} className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-start mb-4">
                    <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800 mb-1">{question.text}</h3>
                      <p className="text-sm text-gray-500">
                        {question.points === 1 ? t('{{count}} point', { count: question.points }) : t('{{count}} points', { count: question.points })}
                        {question.allow_multiple_answers && ` • ${t('Multiple answers allowed')}`}
                      </p>
                    </div>
                  </div>

                  {question.image && (
                    <div className="ml-11 mb-4">
                      <img src={question.image} alt="" className="max-w-full rounded-lg border border-gray-200" />
                    </div>
                  )}

                  <div className="space-y-3 ml-11">
                    {question.answers && question.answers.map((answer) => (
                      <label
                        key={answer.id}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                          (answers[question.id] || []).includes(answer.id)
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-gray-200 hover:border-primary-300'
                        }`}
                      >
                        <input
                          type={question.allow_multiple_answers ? 'checkbox' : 'radio'}
                          name={`question-${question.id}`}
                          checked={(answers[question.id] || []).includes(answer.id)}
                          onChange={() => handleAnswerChange(question.id, answer.id, question.allow_multiple_answers)}
                          className="w-4 h-4 text-primary-600 mr-3"
                        />
                        <span className="text-gray-800">{answer.text}</span>
                      </label>
                    ))}
                  </div>

                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="mt-8 bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div className="text-gray-600">
                  <p className="font-medium">{t('Questions answered: {{answered}} / {{total}}', { answered: Object.keys(answers).length, total: questions.length })}</p>
                  {Object.keys(answers).length < questions.length && (
                    <p className="text-sm text-amber-600 mt-1">
                      ⚠️ {t("You haven't answered all questions")}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleSubmitQuiz(false)}
                  disabled={submitting || Object.keys(answers).length === 0}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? t('Submitting...') : t('Submit Quiz')}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-amber-100 rounded-full mb-4">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
                {t('Submit Quiz?')}
              </h3>
              <p className="text-gray-600 text-center mb-6">
                {t("Are you sure you want to submit your quiz? You won't be able to change your answers after submission.")}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={confirmSubmit}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:opacity-50"
                >
                  {submitting ? t('Submitting...') : t('Submit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TakeQuiz;
